/**
 * useThemeMode — light/dark/system theme preference hook.
 *
 * - `mode` is the user's stated preference (`'light' | 'dark' | 'system'`), persisted to localStorage.
 * - `resolvedMode` is the effective theme (`'light' | 'dark'`) after applying the OS
 *   `prefers-color-scheme` for `mode === 'system'`.
 * - `setMode` writes through to localStorage and updates state.
 *
 * The hook reacts to OS preference changes when `mode === 'system'`.
 */

import { useCallback, useEffect, useState } from 'react';

import type { AppThemeMode } from '@/styles/theme/theme';

export type ThemePreference = AppThemeMode | 'system';

const STORAGE_KEY = 'blend.theme.mode';
const THEME_CHANGE_EVENT = 'blend.theme.changed';
const DEFAULT_THEME_PREFERENCE: ThemePreference = 'light';

function readStoredPreference(): ThemePreference {
  if (globalThis.window === undefined) {
    return DEFAULT_THEME_PREFERENCE;
  }
  const stored = globalThis.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return DEFAULT_THEME_PREFERENCE;
}

function getSystemMode(): AppThemeMode {
  if (globalThis.window === undefined || globalThis.matchMedia === undefined) {
    return 'light';
  }
  return globalThis.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function useThemeMode(): {
  mode: ThemePreference;
  resolvedMode: AppThemeMode;
  setMode: (next: ThemePreference) => void;
} {
  const [mode, setModeState] = useState<ThemePreference>(() => readStoredPreference());
  const [systemMode, setSystemMode] = useState<AppThemeMode>(getSystemMode);

  useEffect(() => {
    if (globalThis.window === undefined || globalThis.matchMedia === undefined) {
      return;
    }
    const mq = globalThis.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemMode(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Sync all hook instances when any one of them writes a new preference.
  useEffect(() => {
    if (globalThis.window === undefined) {
      return;
    }
    const handler = () => setModeState(readStoredPreference());
    globalThis.addEventListener(THEME_CHANGE_EVENT, handler);
    return () => globalThis.removeEventListener(THEME_CHANGE_EVENT, handler);
  }, []);

  const setMode = useCallback((next: ThemePreference) => {
    setModeState(next);
    if (globalThis.window !== undefined) {
      globalThis.localStorage.setItem(STORAGE_KEY, next);
      globalThis.dispatchEvent(new Event(THEME_CHANGE_EVENT));
    }
  }, []);

  const resolvedMode: AppThemeMode = mode === 'system' ? systemMode : mode;

  return { mode, resolvedMode, setMode };
}
